import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useSound from "use-sound";
import { useSelector, useDispatch } from "react-redux";
import { List, ListItem, Avatar, Typography, ListItemText, Collapse } from "@material-ui/core";
// STYLES
import { SidebarWrapper } from "./Sidebar.style";
// ROUTES
import { foreignSidebarRoutes } from "Routes/RoutesList";
// COMPONENTS
import HowToPlay from "Components/ForeignLanguage/RapidFire/Modal/HowToPlay";
// HELPERS
import { Api, FOREIGN_MODULE_ID, commonSidebarListItems, IMAGES } from "Helpers";
import {
	URL_PROFILE,
	URL_CHANGE_PWD,
	URL_ABOUT_US,
	URL_PRIVACY_POLICY,
	URL_TERMS_AND_CONDITIONS,
	URL_FOREIGN_LANGUAGE_DASHBOARD,
	URL_RAPID_FIRE,
	URL_START_QUIZ,
	URL_QUIZ,
	URL_QUIZ_ANSWER,
	URL_CONTACT_US,
	URL_DATA_SECURITY,
} from "Helpers/Paths";
// SOUNDS
import DrawerSound from "Assets/Sounds/switch-off.mp3";
// REDUX
import { logOutUser } from "Redux/Auth/Actions";
import { HowToPlayModal as QuizHowToPlay } from "Components/ForeignLanguage/Quiz/Modal/HowToPlayModal";

const getMenuItemSelected = (currentLocation, route) => {
	switch (currentLocation) {
		case URL_FOREIGN_LANGUAGE_DASHBOARD:
			return route === currentLocation;

		case `${URL_FOREIGN_LANGUAGE_DASHBOARD}/${URL_RAPID_FIRE}`:
			return URL_RAPID_FIRE.includes(route);

		case `${URL_FOREIGN_LANGUAGE_DASHBOARD}/${URL_START_QUIZ}`:
			return URL_START_QUIZ.includes(route);

		case `${URL_FOREIGN_LANGUAGE_DASHBOARD}/${URL_QUIZ}`:
			return URL_QUIZ.includes(route);

		case `${URL_FOREIGN_LANGUAGE_DASHBOARD}/${URL_QUIZ_ANSWER}`:
			return URL_QUIZ_ANSWER.includes(route);

		case `${URL_PROFILE}`:
			return `${URL_PROFILE}` === route;

		case `${URL_ABOUT_US}`:
			return `${URL_ABOUT_US}` === route;

		case `${URL_PRIVACY_POLICY}`:
			return `${URL_PRIVACY_POLICY}` === route;

		case `${URL_TERMS_AND_CONDITIONS}`:
			return `${URL_TERMS_AND_CONDITIONS}` === route;

		case `${URL_DATA_SECURITY}`:
			return `${URL_DATA_SECURITY}` === route;

		case `${URL_CHANGE_PWD}`:
			return `${URL_CHANGE_PWD}` === route;

		case `${URL_CONTACT_US}`:
			return `${URL_CONTACT_US}` === route;

		default:
			return false;
	}
};

const ForeignLanguageSidebar = (props) => {
	const { open } = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const { gamesStatus } = useSelector((state) => state.GamesData);
	const { totalScore, profileInfo, subjectAccessList: subjects, schoolClassTestID } = useSelector((state) => state.Auth);
	const sidebarListItems = profileInfo?.isFromClassLink ? commonSidebarListItems.filter((list) => list.text !== "Change Password") : commonSidebarListItems;

	const [play] = useSound(DrawerSound, { volume: 0.2 });

	const subjectId = subjects?.filter((item) => item.subject === "Foreign Language")[0].subjectId;
	const foreignSidebarRoute = foreignSidebarRoutes.filter((route) => route.order).sort((a, b) => a.order - b.order);
	const routes = location.pathname.includes("foreign-language") && foreignSidebarRoute;

	const [settingsOpen, setSettingsOpen] = useState(false);
	const [rapidFireHowToPlay, setRapidFireHowToPlay] = useState({ open: false, data: {} });
	const [quizardy, setQuizardy] = useState({ open: false, data: {} });

	useEffect(() => {
		if (open) props.toggleDrawer();
	}, [location.pathname]); // eslint-disable-line

	const redirectAndClose = (path) => {
		path = path === "/" + URL_FOREIGN_LANGUAGE_DASHBOARD ? URL_FOREIGN_LANGUAGE_DASHBOARD : path;

		let setStateFunction;

		if (path === "/" + URL_RAPID_FIRE) setStateFunction = setRapidFireHowToPlay;
		if (path === "/" + URL_START_QUIZ) setStateFunction = setQuizardy;

		if (setStateFunction) {
			setStateFunction((prev) => {
				const copyPrev = { ...prev };

				if (!copyPrev.open) {
					props.toggleDrawer();

					console.log("custom path", "/" + URL_START_QUIZ);
					if (path === "/" + URL_RAPID_FIRE) copyPrev.data = { id: "Rapid Fire", subTitle: "RAPID FIRE", url: "rapid-fire" };
					if (path === "/" + URL_START_QUIZ) copyPrev.data = { id: FOREIGN_MODULE_ID.QUIZ, subTitle: "Quizardy", url: URL_START_QUIZ };
				}

				copyPrev.open = !copyPrev.open;

				return copyPrev;
			});
		} else {
			props.toggleDrawer();
			navigate(path);
		}
	};

	const handleLogOut = () => {
		const data = new FormData();

		data.append("token", localStorage.getItem("accessToken"));

		new Api().post("user/logout", { data });

		dispatch(logOutUser());
	};

	const handleClose = ({ url }) => redirectAndClose("/" + url);

	return (
		<>
			<SidebarWrapper
				open={open}
				anchor="left"
				foreign={true}
				location={location}
				onClose={props.toggleDrawer}
				BackdropProps={{ className: "backdrop-root" }}
				classes={{ root: "root", paper: "drawer-paper" }}
			>
				<List>
					<ListItem className="menu-items profile-details">
						<Avatar variant="square" className="img-profile" src={profileInfo.profilePicUrl || IMAGES.PROFILE.BOY} />

						<span className="text">
							{profileInfo.firstName} {profileInfo.lastName}
						</span>

						<Typography variant="body1" className="score">
							{totalScore}
						</Typography>
					</ListItem>

					{routes.map((route, index) => (
						<Fragment key={index}>
							{!route.expand ? (
								<ListItem
									button
									onClick={() => redirectAndClose("/" + route.path)}
									disabled={route?.activeKey ? !gamesStatus?.[route?.activeKey] : false}
									className={`menu-items ${getMenuItemSelected(location.pathname, route.path) ? "active" : ""}`}
								>
									{route.icon ? <img className="icon" src={route.icon} alt={route.name} /> : <img className="icon" src={IMAGES.COMMONS.HOME} alt={route.name} />}
									<ListItemText className="menu-text" primary={route.name} onMouseOver={play} />
								</ListItem>
							) : (
								<>
									<ListItem button className="menu-items" onClick={() => setSettingsOpen(!settingsOpen)}>
										<img className="icon" src={IMAGES.COMMONS.SETTINGS_ICON} alt={route.name} />
										<ListItemText className="menu-text" primary={route.name} onMouseOver={play} />
										{settingsOpen ? (
											<img alt="less-arrow" src={IMAGES.SIDEBAR.LESS_ARROW} height="30px" width="30px" fill="rgba(2, 43, 49, 0.8)" />
										) : (
											<img alt="more-arrow" src={IMAGES.SIDEBAR.MORE_ARROW} height="30px" width="30px" fill="rgba(2, 43, 49, 0.8)" />
										)}
									</ListItem>

									<Collapse in={settingsOpen} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
											{sidebarListItems.map((listItem) => (
												<ListItem
													button
													key={listItem.text}
													onClick={() => redirectAndClose(listItem.clickParams)}
													className={`menu-items settings ${getMenuItemSelected(location.pathname, listItem.clickParams) ? "active" : ""}`}
												>
													<ListItemText className="menu-text" primary={listItem.text} />
												</ListItem>
											))}

											<ListItem button className="menu-items settings" onClick={handleLogOut}>
												<ListItemText className="menu-text" primary={"Logout"} />
											</ListItem>
										</List>
									</Collapse>
								</>
							)}
						</Fragment>
					))}
				</List>
			</SidebarWrapper>

			{rapidFireHowToPlay.open && <HowToPlay open={rapidFireHowToPlay.open} handleClose={handleClose} data={rapidFireHowToPlay.data} subjectId={subjectId} schoolId={schoolClassTestID} />}
			{quizardy.open && <QuizHowToPlay open={quizardy.open} handleClose={handleClose} data={quizardy.data} subjectId={subjectId} schoolId={schoolClassTestID} />}
		</>
	);
};

export default ForeignLanguageSidebar;
